// File: /opt/dev_environment/assets/workspace/app-init/src/pages/VokabelnPage/VocabularyForm.js

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { WORD_CATEGORIES, GRAMMATICAL_GENDERS } from '../../constants/leitner';
import './VokabelnPage.css';

function VocabularyForm({ title, initialData, onSave, onCancel }) {
  const [formData, setFormData] = useState({
    germanWord: initialData?.germanWord || '',
    italianWord: initialData?.italianWord || '',
    gender: initialData?.gender || '',
    category: initialData?.category || '',
    topicTag: initialData?.topicTag || '',
  });

  const germanInputRef = useRef(null);

  // Focus the first input on mount
  useEffect(() => {
    if (germanInputRef.current) {
      germanInputRef.current.focus();
    }
  }, []);

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onCancel();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onCancel]);

  const handleChange = useCallback((field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    if (!formData.germanWord.trim() || !formData.italianWord.trim()) return;
    onSave({
      ...formData,
      germanWord: formData.germanWord.trim(),
      italianWord: formData.italianWord.trim(),
      topicTag: formData.topicTag.trim(),
    });
  }, [formData, onSave]);

  const handleOverlayClick = useCallback((e) => {
    if (e.target === e.currentTarget) {
      onCancel();
    }
  }, [onCancel]);

  const isValid = formData.germanWord.trim() !== '' && formData.italianWord.trim() !== '';

  return (
    <div
      className="vokabeln-dialog-overlay"
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div className="vokabeln-dialog">
        <div className="vokabeln-dialog__header">
          <h2 className="vokabeln-dialog__title">{title}</h2>
          <button
            className="vokabeln-dialog__close"
            type="button"
            onClick={onCancel}
            aria-label="Dialog schließen"
          >
            ✕
          </button>
        </div>

        <form className="vokabeln-dialog__body" onSubmit={handleSubmit}>
          {/* German Word */}
          <div className="vokabeln-form-field">
            <label className="vokabeln-form-field__label" htmlFor="vocab-german">
              Deutsch *
            </label>
            <input
              ref={germanInputRef}
              id="vocab-german"
              className="vokabeln-form-field__input"
              type="text"
              value={formData.germanWord}
              onChange={(e) => handleChange('germanWord', e.target.value)}
              placeholder="z.B. Haus"
              required
              autoComplete="off"
            />
          </div>

          {/* Italian Word */}
          <div className="vokabeln-form-field">
            <label className="vokabeln-form-field__label" htmlFor="vocab-italian">
              Italienisch *
            </label>
            <input
              id="vocab-italian"
              className="vokabeln-form-field__input"
              type="text"
              value={formData.italianWord}
              onChange={(e) => handleChange('italianWord', e.target.value)}
              placeholder="z.B. la casa"
              required
              autoComplete="off"
            />
          </div>

          {/* Gender */}
          <div className="vokabeln-form-field">
            <label className="vokabeln-form-field__label" htmlFor="vocab-gender">
              Geschlecht
            </label>
            <select
              id="vocab-gender"
              className="vokabeln-form-field__select"
              value={formData.gender}
              onChange={(e) => handleChange('gender', e.target.value)}
            >
              <option value="">— Kein Geschlecht —</option>
              {GRAMMATICAL_GENDERS.map((g) => (
                <option key={g.value} value={g.value}>
                  {g.label}
                </option>
              ))}
            </select>
          </div>

          {/* Category */}
          <div className="vokabeln-form-field">
            <label className="vokabeln-form-field__label" htmlFor="vocab-category">
              Kategorie
            </label>
            <select
              id="vocab-category"
              className="vokabeln-form-field__select"
              value={formData.category}
              onChange={(e) => handleChange('category', e.target.value)}
            >
              <option value="">— Kategorie wählen —</option>
              {WORD_CATEGORIES.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>

          {/* Topic Tag */}
          <div className="vokabeln-form-field">
            <label className="vokabeln-form-field__label" htmlFor="vocab-topic">
              Thema (optional)
            </label>
            <input
              id="vocab-topic"
              className="vokabeln-form-field__input"
              type="text"
              value={formData.topicTag}
              onChange={(e) => handleChange('topicTag', e.target.value)}
              placeholder="z.B. Baustelle, Alltag"
              autoComplete="off"
            />
          </div>

          {/* Actions */}
          <div className="vokabeln-dialog__actions">
            <button
              className="vokabeln-dialog__btn vokabeln-dialog__btn--cancel"
              type="button"
              onClick={onCancel}
            >
              Abbrechen
            </button>
            <button
              className="vokabeln-dialog__btn vokabeln-dialog__btn--save"
              type="submit"
              disabled={!isValid}
            >
              Speichern
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default VocabularyForm;